import React, { useState, useEffect, useCallback } from "react";
import ConnectButton from "./ConnectButton";
import { ContextProvider } from "./web3ModalConfig";
import styles from "./WalletConnect.module.css";
import projectLogo from "./logo.jpg";

const WEBSITE_URL = "https://xmaquina-verify.com"; // For Production
// const WEBSITE_URL = "http://localhost:3001"; // For Development (backend port)

// Maps the `flow` query param to the backend endpoint that grants the
// corresponding role(s). 'og' is the default and matches the legacy flow.
const FLOW_ENDPOINTS = {
  og: "/api/users",
  humanoid: "/api/users/humanoid",
};

const FLOW_DESCRIPTIONS = {
  og:
    "You'll need to hold at least 1 $DEUS token (Peaq or Base), 1 Contributor token (Base or Solana), or 1 Vote Escrow DEUS (DAO Member) NFT to get a role.",
  humanoid:
    "You'll need to hold a Titanium, Steel, or Iron Humanoid NFT on Base to claim your role.",
};

const FLOW_TITLES = {
  og: "Token Holder Verification",
  humanoid: "Humanoid Card Holder Verification",
};

function App() {
  const [status, setStatus] = useState("");
  const [token, setToken] = useState(null);
  const [flow, setFlow] = useState("og");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get("token");
    const flowParam = urlParams.get("flow") || "og";
    setToken(tokenParam);
    setFlow(FLOW_ENDPOINTS[flowParam] ? flowParam : "og");
  }, []);

  const sendWalletAddress = useCallback(
    async (walletAddress, currentToken, currentFlow, chain) => {
      const endpoint = FLOW_ENDPOINTS[currentFlow] || FLOW_ENDPOINTS.og;
      try {
        const response = await fetch(`${WEBSITE_URL}${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: currentToken, walletAddress, chain }),
        });
        const data = await response.json();
        setStatus(data.message || "Done.");
      } catch (error) {
        console.error("Error:", error);
        setStatus("Failed to send wallet address.");
      }
    },
    []
  );

  const verifyToken = useCallback(
    async (walletAddress, currentToken, namespace) => {
      // Backend distinguishes Solana (SPL Contributor mint) from EVM chains.
      const chain = namespace === "solana" ? "solana" : "evm";
      try {
        const response = await fetch(`${WEBSITE_URL}/verify-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: currentToken }),
        });

        if (!response.ok) {
          setStatus("JWT expired. Please go back to Discord.");
          return;
        }

        const data = await response.json();
        const tokenFlow = data.flow || "og";

        if (tokenFlow !== flow) {
          setStatus(
            "This verification link is for a different role and cannot be used here."
          );
          return;
        }

        setStatus("Checking token balance...");
        sendWalletAddress(walletAddress, currentToken, tokenFlow, chain);
      } catch (error) {
        console.error("Error:", error);
        setStatus(error.message);
      }
    },
    [flow, sendWalletAddress]
  );

  return (
    <ContextProvider>
      <div className={styles.container}>
        <img src={projectLogo} alt="Main Logo" className={styles.logo} />
        <h1 className={styles.title}>{FLOW_TITLES[flow] || FLOW_TITLES.og}</h1>
        <ConnectButton
          verifyToken={verifyToken}
          setStatus={setStatus}
          token={token}
        />
        {status && (
          <div className={styles.statuscard}>
            <p>{status}</p>
          </div>
        )}
        {!status && (
          <div className={styles.descriptioncard}>
            <p>{FLOW_DESCRIPTIONS[flow] || FLOW_DESCRIPTIONS.og}</p>
          </div>
        )}
      </div>
    </ContextProvider>
  );
}

export default App;
